import React, { useState, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, TextField, Typography, InputAdornment, IconButton, Link, Paper, CircularProgress } from "@material-ui/core";
import { Visibility, VisibilityOff, CheckCircleOutline, LockOutlined } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import BrandFooter from "../../components/BrandFooter";
import logo from "../../assets/zapticket_ligth.png";

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: "100vh",
    "@supports (min-height: 100dvh)": { minHeight: "100dvh" },
    display: "flex", flexDirection: "column",
    background: "#f3f7f5",
    color: "#172c25",
  },
  body: {
    flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr",
    width: "100%", minWidth: 0,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
  },
  intro: {
    background: "radial-gradient(ellipse at top left, #147c56, transparent 70%), linear-gradient(145deg, #103e30, #071e18)",
    color: "#fff", padding: "clamp(32px, 6vw, 96px)",
    display: "flex", flexDirection: "column", justifyContent: "center",
    [theme.breakpoints.down("sm")]: { padding: "32px 24px" },
  },
  logo: { width: 220, maxWidth: "100%", objectFit: "contain", marginBottom: 40 },
  eyebrow: { color: "#91e8b6", fontSize: 12, fontWeight: 700, letterSpacing: 2, marginBottom: 16 },
  headline: { fontSize: "clamp(28px, 3.7vw, 52px)", lineHeight: 1.12, fontWeight: 700, maxWidth: 520 },
  description: { marginTop: 20, color: "#c9ded5", lineHeight: 1.7, maxWidth: 460 },
  features: {
    listStyle: "none", padding: 0, margin: "32px 0 0",
    "& li": { display: "flex", alignItems: "center", gap: 12, marginBottom: 16, fontSize: 14 },
    "& svg": { color: "#91e8b6", fontSize: 20 },
    [theme.breakpoints.down("sm")]: { display: "none" },
  },
  formArea: {
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "48px 32px", minWidth: 0,
    [theme.breakpoints.down("sm")]: { padding: "24px 16px" },
  },
  card: { width: "100%", maxWidth: 440, padding: 36, borderRadius: 20, background: "#fff", color: "#172c25",
    boxShadow: "0 16px 60px rgba(16,62,48,.08)", border: "1px solid #e2ebe6",
    [theme.breakpoints.down("xs")]: { padding: 24 },
    "& .MuiInputBase-root": { color: "#172c25", background: "#fff" },
    "& .MuiInputLabel-root": { color: "#52685e" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c2d0c8" },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#137a50" },
    "& .MuiInputLabel-root.Mui-focused": { color: "#137a50" },
  },
  icon: { color: "#137a50", background: "#e8f6ee", borderRadius: 14, padding: 12, display: "inline-flex", marginBottom: 20 },
  title: { fontWeight: 700, marginBottom: 8 },
  subtitle: { color: "#61756b", fontSize: 14, marginBottom: 20 },
  form: { width: "100%", "& input": { fontSize: 16 } },
  submit: { margin: "24px 0 20px", minHeight: 48, borderRadius: 10, fontWeight: 700,
    background: "#137a50", color: "#fff", boxShadow: "none",
    "&:hover": { background: "#0e603e", boxShadow: "none" } },
  register: { textAlign: "center", fontSize: 14, color: "#61756b",
    "& a": { color: "#137a50", fontWeight: 700 } },
}));

export default function Login() {
  const classes = useStyles();
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { handleLogin } = useContext(AuthContext);
  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try { await handleLogin({ ...user, email: user.email.trim() }); }
    finally { setSubmitting(false); }
  };
  return (
    <div className={classes.root}>
      <main className={classes.body}>
        <section className={classes.intro} aria-label="ZapTicket">
          <img src={logo} alt="ZapTicket" className={classes.logo} />
          <Typography className={classes.eyebrow}>ATENDIMENTO QUE CONECTA</Typography>
          <Typography component="h1" className={classes.headline}>Mais proximidade.<br />Mais organização.</Typography>
          <Typography className={classes.description}>Um só lugar para organizar suas conversas, conectar sua equipe e cuidar de cada atendimento.</Typography>
          <ul className={classes.features}>
            {["Conversas organizadas", "Equipe trabalhando em conjunto", "Agilidade em cada atendimento"].map(text =>
              <li key={text}><CheckCircleOutline aria-hidden="true" />{text}</li>
            )}
          </ul>
        </section>
        <section className={classes.formArea} aria-labelledby="login-title">
          <Paper elevation={0} className={classes.card}>
            <div className={classes.icon}><LockOutlined aria-hidden="true" /></div>
            <Typography component="h2" variant="h5" id="login-title" className={classes.title}>Bem-vindo ao ZapTicket</Typography>
            <Typography className={classes.subtitle}>Entre com seu e-mail e senha para continuar.</Typography>
            <form className={classes.form} onSubmit={handleSubmit} aria-busy={submitting}>
              <TextField variant="outlined" margin="normal" required fullWidth id="email"
                type="email" label={i18n.t("login.form.email")} name="email" value={user.email}
                onChange={e => setUser({ ...user, email: e.target.value })}
                autoComplete="username" inputProps={{ autoCapitalize: "none", spellCheck: false }} />
              <TextField variant="outlined" margin="normal" required fullWidth id="password"
                name="password" label={i18n.t("login.form.password")} value={user.password}
                onChange={e => setUser({ ...user, password: e.target.value })}
                autoComplete="current-password" type={showPassword ? "text" : "password"}
                InputProps={{ endAdornment: <InputAdornment position="end">
                  <IconButton aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    aria-pressed={showPassword} onMouseDown={e => e.preventDefault()}
                    onClick={() => setShowPassword(!showPassword)} style={{ color: "#52685e" }}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment> }} />
              <Button type="submit" fullWidth variant="contained" disabled={submitting} className={classes.submit}>
                {submitting ? <><CircularProgress size={18} color="inherit" style={{ marginRight: 10 }} />Entrando…</> : i18n.t("login.buttons.submit")}
              </Button>
              <div className={classes.register}>
                <Link component={RouterLink} to="/signup">{i18n.t("login.buttons.register")}</Link>
                <Link href="/assinatura" style={{ display: "block", marginTop: 12 }}>Minha assinatura</Link>
              </div>
            </form>
          </Paper>
        </section>
      </main>
      <BrandFooter light />
    </div>
  );
}
